import draggable from 'vuedraggable';
import { mapActions, mapGetters, mapMutations, mapState } from 'vuex';

import getIsTouchDeviceMixins from '@/mixins/check-is-touch-device';
import ContentSyncMixin from '@/mixins/content-sync';
import commonMixins from '@/mixins/templates/common';
import courseMixins from '@/mixins/templates/course';

import ContentNotAvailable from '@/components/content-v2/content-not-available';
import ModalContentTypeLTI from '@/components/content-v2/modals/render-lti';
import SelectContentType from '@/components/content-v2/select-content-type';

import NoticeCarousel from '@/components/comunication/notice-carousel';
import AvailabilityBanner from '@/components/content-v2/availability-banner';
import PageHeader from '@/components/content-v2/templates/common/page-header';
import CardTopic from '@/components/content-v2/templates/timeline/card-topic';
import TopicSettingsMenu from '@/components/content-v2/templates/common/topic-settings-menu';
import SyncSummaryListDetail from '@/components/content-v2/modals/content-sync/sync-summary-list-detail';
import RejectApprovalModal from '@/components/content-v2/modals/reject-approval-modal';
import MaxModuleLimit from '@/components/content-v2/templates/common/max-module-limit';
import PendingApprovalBanner from '@/components/content-v2/templates/common/pending-approval-banner';
import EmptyAssociatedWarning from '@/components/ui/empty-associated-warn';
import SyncStatus from '@/components/content-v2/modals/content-sync/sync-status';
import FloatingSupport from '~/components/_core/ui/floating-support/index.vue';

import AcademicMainTypes from '@/helpers/academicMainType';
import ApprovalStatus from '@/helpers/approvalStatus';
import CategoryType from '@/helpers/contentCategory';
import ContentType from '@/helpers/contentType';
import TemplateType from '@/helpers/templateType';

const EXPANSION_STATUS_CLASS = {
  completed  : 'completed',
  inProgress : 'in-progress',
  comingSoon : 'coming-soon'
};

export default {
  mixins: [courseMixins, commonMixins, getIsTouchDeviceMixins, ContentSyncMixin],
  data() {
    return {
      EXPANSION_STATUS_CLASS,
      ContentType,
      CategoryType,
      TemplateType,
      AcademicMainTypes,
      ApprovalStatus,
      isLoading         : false,
      addContentOptions : [
        { text: 'content.elementOptions.newModuleGroup', icon: 'mdi-folder-plus-outline', func: () => this.createModuleGroup(), permissionAlias: 'resources_folder_add' },
        { text: 'content.elementOptions.newModule', icon: 'mdi-post-outline', func: (topic) => (this.createModule(topic)), permissionAlias: 'resources_page_add' },
        { text: 'content.elementOptions.newContentOrActivity', icon: 'mdi-toy-brick-plus-outline', func: (topic) => this.emitCreateContentOrExercise(topic), permissionAliases: ContentType.getPermissions('add') }
      ],
      panelActivate           : null,
      hasInconsistencyInGrade : true,
      hasHighlightNotices     : false,
      observers               : []
    };
  },
  components: {
    CardTopic,
    draggable,
    SelectContentType,
    ContentNotAvailable,
    ModalContentTypeLTI,
    PageHeader,
    AvailabilityBanner,
    TopicSettingsMenu,
    NoticeCarousel,
    RejectApprovalModal,
    MaxModuleLimit,
    PendingApprovalBanner,
    SyncSummaryListDetail,
    EmptyAssociatedWarning,
    SyncStatus,
    FloatingSupport
  },
  computed: {
    ...mapGetters({
      getterCourse                       : 'plataforma/newContent/getterCourse',
      getName                            : '_core/user/getName',
      getEmail                           : '_core/user/getEmail',
      getterUserRoleContext              : 'plataforma/user/getterUserContext',
      isImpersonated                     : '_core/auth/isImpersonated',
      getterHasInconsistencyInCategories : 'plataforma/grades/getterHasInconsistencyInCategories'
    }),
    ...mapState({
      user      : (state) => state._core.user,
      roleAlias : state => state._core.auth.currentRole.role.alias
    }),
    isCommunity() {
      return this.getterCourse.context?.academicMainTypeId === AcademicMainTypes.COMMUNITY;
    },
    getPermissionAliases() {
      return [
        ...this.addContentOptions.filter(option => option.permissionAlias).map(option => option.permissionAlias),
        ...this.addContentOptions.filter(option => option.permissionAliases)
          .map(option => option.permissionAliases).reduce(option => option)
      ];
    },
    summaryTitle() {
      return this.isMasterCourse && !this.getterUserRoleContext.isStudent
        ? this.$t('content.layout.timeline.course.masterCourseSummary')
        : this.getSummaryText();
    },
    isSyncStatusEnabled() {
      return this.getterUserRoleContext.isPublisher && (this.isAssociatedCourse || (this.isMasterCourse && this.getterCourse?.associatedAcademicMains?.length && this.getterCourse?.topics?.length));
    }
  },
  methods: {
    ...mapActions({
      actionSaveNewTopic      : 'plataforma/newContent/actionSaveNewTopic',
      actionPatchReorderTopic : 'plataforma/newContent/actionPatchReorderTopic',
      actionPatchTitleTopic   : 'plataforma/newContent/actionPatchTitleTopic',
      actionAddProgress       : 'plataforma/newContent/actionAddProgress',
      actionWebconferenceURL  : 'plataforma/newContent/actionWebconferenceURL'
    }),
    ...mapMutations({
      NEW_TOPIC                                  : 'plataforma/newContent/NEW_TOPIC',
      SET_IS_EDITING_TOPIC                       : 'plataforma/newContent/SET_IS_EDITING_TOPIC',
      CLEAR_SELECTED_COURSE                      : 'plataforma/newContent/CLEAR_SELECTED_COURSE',
      UPDATE_TOPIC_VERSION_AT_ON_SEQUENCE_CHANGE : 'plataforma/newContent/UPDATE_TOPIC_VERSION_AT_ON_SEQUENCE_CHANGE'
    }),
    handlerExpansion(element, id) {
      const existingObserver = this.observers.find((obs) => obs.id === id);
      if (!existingObserver) {
        const expansionParent = element.target.offsetParent.offsetParent;
        const expansionElement = element.target.offsetParent;

        const observer = new MutationObserver((mutationsList) => {
          mutationsList.forEach((mutation) => {
            if (mutation.type === 'attributes' && mutation.attributeName === 'aria-expanded') {
              const expandedValue = expansionParent.getAttribute('aria-expanded');

              expansionElement.setAttribute('aria-expanded', expandedValue);
            }
          });
        });

        observer.observe(expansionParent, { attributes: true });
        this.observers.push({ id, observer });
      }
    },
    welcomeText() {
      const hour = new Date().getHours();

      if (hour < 12) {
        return this.$t('content.layout.timeline.course.goodMorning');
      } else if (hour < 18) {
        return this.$t('content.layout.timeline.course.goodAfternoon');
      } else {
        return this.$t('content.layout.timeline.course.goodEvening');
      }
    },
    openModalTopicTypes(topic) {
      const params = {
        academicMainId     : this.getterCourse.context.academicMainId,
        academicMainTypeId : this.getterCourse.context.academicMainTypeId,
        parentTopic        : topic
      };

      this.$nuxt.$emit('changeIsOpenModalContentTypes', params);
    },
    openModalTopicDetails(topic) {
      switch (topic.categoryTypeId) {
        case CategoryType.WEBCONFERENCE:
        case CategoryType.LTI:
        case CategoryType.MENU_INTERACTIVES:
        case CategoryType.AVALIA:
        case CategoryType.SCORM:
          this.$nuxt.$emit('changeIsOpenModalTopicDetail', {
            topic,
            recordList     : null,
            academicMainId : this.getterCourse.context.academicMainId
          });
          break;
      }
    },
    changeOrder(topic) {
      const params = {
        academicMainId : this.$route.params.courseId,
        topic          : topic.moved.element,
        newSequence    : topic.moved.newIndex,
        oldSequence    : topic.moved.oldIndex,
        version        : this.getterCourse.context.version,
        parentTopicId  : topic.moved.element.parentTopicId
      };

      this.$nuxt.$loading.start();

      this.actionPatchReorderTopic(params)
        .then(() => {
          if (this.isMasterCourse) {
            this.UPDATE_TOPIC_VERSION_AT_ON_SEQUENCE_CHANGE({
              parentTopicId : params.parentTopicId,
              oldIndex      : params.oldSequence,
              newIndex      : params.newSequence
            });
          }

          this.$toast.success(this.$t('content.successMessagesSnackbar.changeSequence'));
        }).catch((err) => {
          this.$toast.error(err.data.error.message);
        }).finally(() => {
          this.$nuxt.$loading.finish();
        });
    },
    createModuleGroup() {
      const params = {
        title                              : '',
        categoryTypeId                     : CategoryType.ORGANIZATION,
        topicTypeId                        : ContentType.FOLDER,
        parentTopicId                      : null,
        isEditing                          : true,
        hasConditional                     : false,
        isVisibleIsControlledByConditional : false,
        conditionals                       : []
      };

      this.NEW_TOPIC(params);
    },
    changeIsEditingTopic(index) {
      this.SET_IS_EDITING_TOPIC(index);
    },
    saveTopicTitle(topic, index) {
      if (!topic.id) {
        const params = {
          topic: {
            design  : {},
            ...topic,
            version : this.getterCourse.context.version
          },
          academicMainId : this.$route.params.courseId,
          isToReplace    : true,
          index
        };

        delete params.topic.isEditing;

        const indexToOpen = this.computedTopics.length - 1;

        this.createTopic(params, indexToOpen);
      } else {
        const params = {
          topicId        : topic.id,
          academicMainId : this.$route.params.courseId,
          title          : topic.title,
          version        : this.getterCourse.context.version
        };

        this.changeTopicTitle(params);
      }
    },
    createTopic(params, idExpansionPanelToOpen = null) {
      this.$nuxt.$loading.start();

      this.actionSaveNewTopic(params)
        .then(() => {
          this.$toast.success(this.$t('content.successMessagesSnackbar.createGroupModule'));

          if (idExpansionPanelToOpen !== null) {
            this.panelActivate = idExpansionPanelToOpen;

            setTimeout(() => {
              window.scrollTo(0, document.body.scrollHeight);
            }, 350);
          }
        }).catch((err) => {
          this.$toast.error(err.message);
        }).finally(() => {
          this.$nuxt.$loading.finish();
        });
    },
    changeTopicTitle(params) {
      this.$nuxt.$loading.start();

      this.actionPatchTitleTopic(params)
        .then(() => {
          this.$toast.success(this.$t('content.successMessagesSnackbar.changeTitle'));
        }).catch((err) => {
          this.$toast.error(err.data.error.message);
        }).finally(() => {
          this.$nuxt.$loading.finish();
        });
    },
    isToShowDashedLine(topic, index) {
      return index > 0 &&
        ((
          topic.topicTypeId !== ContentType.FOLDER && this.computedTopics[index - 1].topicTypeId === ContentType.FOLDER && (topic.parentTopicId === null || topic.topicTypeId === ContentType.PAGE)
        ) ||
          (topic.topicTypeId === ContentType.FOLDER && this.computedTopics[index - 1].topicTypeId !== ContentType.FOLDER)
        );
    },
    checkExpansionStatus(topic) {
      if (this.getterCourse.context.academicMainTypeId === AcademicMainTypes.COMMUNITY) {
        return false;
      }
      let totalItems = 0;
      let viewedItems = 0;

      if (topic.children && topic.children.length) {
        topic.children.map(item => {
          if (item.categoryTypeId === CategoryType.ORGANIZATION) {
            totalItems += item.childrenCount;

            if (item.isRecordProgress) {
              viewedItems += item.viewedChildrenCount;
            }
          } else {
            item.isRecordProgress && totalItems++;
            viewedItems += item.viewed ? 1 : 0;
          }
        });
      }

      if (totalItems && viewedItems && (totalItems === viewedItems)) {
        return { text: 'completed', class: 'completed' };
      } else if (totalItems && viewedItems && (totalItems > viewedItems)) {
        return { text: 'inProgress', class: 'in-progress' };
      } else if (!(topic.children && topic.children.length) && !totalItems && !viewedItems) {
        return { text: 'comingSoon', class: 'coming-soon' };
      } else {
        return false;
      }
    },
    // scrollToTopic({ newParentTopic, topic }) {
    //   console.log('chamou', newParentTopic, topic);
    //   if (newParentTopic && newParentTopic.id) {
    //     const index = this.getterCourse.topics.findIndex(t => t.id === newParentTopic.id);

    //     if (index > -1) {
    //       this.panelActivate = index;

    //       setTimeout(() => {
    //         const element = document.getElementById(topic.id);

    //         if (element) {
    //           element.scrollIntoView({ block: 'center', behavior: 'smooth' });
    //         }
    //       }, 500);
    //     }
    //   } else {
    //     const element = document.getElementById(topic.id);

    //     if (element) {
    //       element.scrollIntoView({ block: 'center', behavior: 'smooth' });
    //     }
    //   }
    // },
    hasShowVerticalDivider(topic) {
      if (
        ((!topic.hasOwnProperty('children') || !topic.children || !topic.children.length) && !topic.isEditing) ||
        (!topic.isVisible && !topic.isEditing) ||
        (topic.conditionals?.length)
      ) {
        return true;
      }

      return false;
    },
    showConditionalAvailability(topic) {
      this.$nuxt.$emit('changeIsOpenConditionalsListModal', {
        conditionals        : topic.conditionals,
        isAccessCodeEnabled : topic.isAccessCodeEnabled
      });
    },
    isStudentAndIsBlockedContent(topic) {
      return this.getterUserRoleContext.isStudent && topic.isBlockedContentByConditional;
    },
    isFeatureLocked(feature) {
      return process.env.FEATURES_LOCK?.includes(feature);
    },
    openEvaluationsCenter() {
      this.$nuxt.$emit('changeIsOpenModalEvaluations', {
        showModal              : true,
        contentId              : this.getterCourse.context.contentId,
        academicMainId         : this.getterCourse.context.academicMainId,
        academicMainExternalId : this.getterCourse.context.academicMainExternalId,
        academicMainTypeId     : this.getterCourse.context.academicMainTypeId
      });
    },
    openParticipants() {
      this.$refs.pageHeader.participantManagementModalConfig = {
        isOpen                 : true,
        courseId               : this.getterCourse.context.academicMainId,
        academicMainId         : this.getterCourse.context.academicMainId,
        academicMainName       : this.getterCourse.context.academicMainName,
        academicMainTypeId     : this.getterCourse.context.academicMainTypeId,
        academicMainExternalId : this.getterCourse.context.academicMainExternalId
      };
    },
    openSyncModal() {
      this.$nuxt.$emit('changeIsOpenModalContentSync', { academicMainId: this.getterCourse.context.academicMainId });
    },
    openImportModal() {
      this.$nuxt.$emit('changeIsOpenModalContentImport', { academicMainIdDestination: this.getterCourse.context.academicMainId, academicMainDestinationName: this.getterCourse.context.academicMainName });
    }
  },
  beforeDestroy() {
    this.CLEAR_SELECTED_COURSE();
    this.$nuxt.$off('changeModalConditionalAvailability');
    this.observers.forEach(({ observer }) => observer.disconnect());
  }
};
